import type { MeasuredAttempt } from "../../domain/challenge/measured-types.js";
import type { SetHoleBonus } from "../../domain/contest/hole-bonus.js";
import {
  applyRoundCommand,
  createRoundFromCommand,
  type CreateRoundCommandPayload,
  type RoundCommandEvent,
  type RoundConflictSnapshot,
  type RoundCommandPayload,
  type RoundCommandType,
  type RoundSnapshot,
} from "../../domain/round/round-reducer.js";
import type { RoundJournal } from "../data/round-journal.js";
import { holeEntryInput } from "../lib/hole-entry-input.js";
import {
  isRoundV3,
  type HoleEntryInput,
} from "../../domain/round/round-types.js";

export interface RoundSyncApi {
  sync(events: RoundCommandEvent[]): Promise<{
    acknowledgedEventIds: string[];
    snapshot: RoundSnapshot;
    conflicts: RoundConflictSnapshot[];
  }>;
}

export interface RoundSyncEngineOptions {
  journal: RoundJournal;
  api: RoundSyncApi;
  accountId: string;
  deviceId: string;
  nextSequence: () => number;
  nextEventId: () => string;
  now?: () => Date;
}

export type CreateLocalRoundInput = CreateRoundCommandPayload & {
  roundId: string;
};

export class RoundSyncEngine {
  private readonly now: () => Date;
  private conflicts: RoundConflictSnapshot[] = [];
  private pendingMutation: Promise<void> = Promise.resolve();
  private pendingFlush: Promise<void> = Promise.resolve();

  public constructor(private readonly options: RoundSyncEngineOptions) {
    this.now = options.now ?? (() => new Date());
  }

  public async createRound(
    input: CreateLocalRoundInput,
  ): Promise<RoundSnapshot> {
    const { roundId, ...payload } = input;
    const event = this.createEvent(roundId, 0, "round.created", payload);
    const snapshot = createRoundFromCommand(event, this.options.accountId);
    await this.options.journal.saveSnapshotAndEnqueue(
      this.options.accountId,
      snapshot,
      event,
    );
    return snapshot;
  }

  public startRound(roundId: string): Promise<RoundSnapshot> {
    return this.applyLocal(roundId, "round.started", {});
  }

  public setHoleBonus(
    roundId: string,
    bonus: SetHoleBonus,
  ): Promise<RoundSnapshot> {
    return this.applyLocal(roundId, "contest.hole-bonus.set", bonus);
  }

  public setScore(
    roundId: string,
    participantId: string,
    holeNumber: number,
    strokes: number,
  ): Promise<RoundSnapshot> {
    return this.applyLocal(roundId, "score.set", {
      targetId: participantId,
      holeNumber,
      strokes,
    });
  }

  public setMeasuredAttempt(
    roundId: string,
    participantId: string,
    attemptNumber: number,
    entry: MeasuredAttempt,
  ): Promise<RoundSnapshot> {
    return this.applyLocal(roundId, "measured.attempt.set", {
      participantId,
      attemptNumber,
      entry: { status: entry.status, value: entry.value },
    });
  }

  public setMetric(
    roundId: string,
    participantId: string,
    value: number,
  ): Promise<RoundSnapshot> {
    return this.applyLocal(roundId, "metric.set", { participantId, value });
  }

  public setHoleEntry(
    roundId: string,
    participantId: string,
    holeNumber: number,
    entry: HoleEntryInput,
  ): Promise<RoundSnapshot> {
    return this.applyLocal(roundId, "hole.entry.set", {
      targetId: participantId,
      holeNumber,
      entry: holeEntryInput(entry),
    });
  }

  public declareWinner(
    roundId: string,
    winnerParticipantIds: string[],
  ): Promise<RoundSnapshot> {
    return this.applyLocal(roundId, "result.declared", {
      winnerParticipantIds,
    });
  }

  public reviseHoleEntry(
    roundId: string,
    participantId: string,
    holeNumber: number,
    entry: HoleEntryInput,
    reviewedVersion: number,
  ): Promise<RoundSnapshot> {
    return this.applyLocal(roundId, "hole.entry.revised", {
      targetId: participantId,
      holeNumber,
      entry: holeEntryInput(entry),
      reviewedVersion,
    });
  }

  public concedeHole(
    roundId: string,
    contestId: string,
    holeNumber: number,
    concedingSideId: string,
  ): Promise<RoundSnapshot> {
    return this.applyLocal(roundId, "contest.hole.conceded", {
      contestId,
      holeNumber,
      concedingSideId,
    });
  }

  public concedeMatch(
    roundId: string,
    contestId: string,
    concedingSideId: string,
  ): Promise<RoundSnapshot> {
    return this.applyLocal(roundId, "contest.match.conceded", {
      contestId,
      concedingSideId,
    });
  }

  public agreeRemainder(
    roundId: string,
    id: string,
    parentContestId: string,
    holeNumbers: number[],
    importedAgreement?: true,
  ): Promise<RoundSnapshot> {
    return this.applyLocal(roundId, "contest.remainder.agreed", {
      id,
      parentContestId,
      holeNumbers,
      ...(importedAgreement ? { importedAgreement } : {}),
    });
  }

  public finishRound(roundId: string): Promise<RoundSnapshot> {
    return this.applyLocal(roundId, "round.finished", {});
  }

  /** Keep the handover intent durable; only the server changes local authority. */
  public transferScorekeeper(
    roundId: string,
    nextScorekeeperId: string,
  ): Promise<RoundSnapshot> {
    const next = this.pendingMutation.then(async () => {
      const current = await this.requireSnapshot(roundId);
      if (
        !isRoundV3(current) ||
        current.state !== "active" ||
        current.scorekeeperId !== this.options.accountId
      ) {
        throw new Error(
          "Only the current scorekeeper can transfer an active round.",
        );
      }
      if (
        (await this.options.journal.listQueued(this.options.accountId, roundId))
          .length
      ) {
        throw new Error(
          "Sync every saved change before transferring scorekeeping.",
        );
      }
      const event = this.createEvent(
        roundId,
        current.version,
        "scorekeeper.transferred",
        {
          nextScorekeeperId,
          expectedAcknowledgedVersion: current.version,
        },
        current.scorekeeperGeneration,
      );
      await this.options.journal.enqueue(this.options.accountId, event);
    });
    this.pendingMutation = next.then(
      () => undefined,
      () => undefined,
    );
    return next.then(() => this.flush(roundId));
  }

  public abandonRound(roundId: string): Promise<RoundSnapshot> {
    return this.applyLocal(roundId, "round.abandoned", {});
  }

  public flush(roundId: string): Promise<RoundSnapshot> {
    const next = this.pendingFlush.then(() => this.flushQueued(roundId));
    this.pendingFlush = next.then(
      () => undefined,
      () => undefined,
    );
    return next;
  }

  private async flushQueued(roundId: string): Promise<RoundSnapshot> {
    await this.pendingMutation;
    const events = await this.options.journal.listQueued(
      this.options.accountId,
      roundId,
    );
    if (events.length === 0) {
      return this.requireSnapshot(roundId);
    }
    const result = await this.options.api.sync(events);
    this.conflicts = result.conflicts;
    if (result.snapshot.roundId !== roundId) {
      throw new Error("The server returned a different round.");
    }
    const stored = await this.options.journal.acceptServerSnapshot(
      this.options.accountId,
      result.snapshot,
      {
        sentEventIds: events.map((event) => event.eventId),
        acknowledgedEventIds: result.acknowledgedEventIds,
      },
    );
    return stored === "server"
      ? result.snapshot
      : this.requireSnapshot(roundId);
  }

  public getConflicts(): RoundConflictSnapshot[] {
    return [...this.conflicts];
  }

  public clearConflicts(): void {
    this.conflicts = [];
  }

  private applyLocal(
    roundId: string,
    type: RoundCommandType,
    payload: RoundCommandPayload,
  ): Promise<RoundSnapshot> {
    // Rapid taps must read the snapshot saved by the previous local command.
    const next = this.pendingMutation.then(() =>
      this.persistLocal(roundId, type, payload),
    );
    this.pendingMutation = next.then(
      () => undefined,
      () => undefined,
    );
    return next;
  }

  private async persistLocal(
    roundId: string,
    type: RoundCommandType,
    payload: RoundCommandPayload,
  ): Promise<RoundSnapshot> {
    const current = await this.requireSnapshot(roundId);
    if (current.scorekeeperId !== this.options.accountId) {
      throw new Error(
        "Scorekeeping has moved to another player. You can follow the round here.",
      );
    }
    const queued = await this.options.journal.listQueued(
      this.options.accountId,
      roundId,
    );
    if (queued.some((event) => event.type === "scorekeeper.transferred")) {
      throw new Error(
        "Sync the pending scorekeeper transfer before entering more scores.",
      );
    }
    const event = this.createEvent(
      roundId,
      current.version,
      type,
      payload,
      isRoundV3(current) ? current.scorekeeperGeneration : undefined,
    );
    const snapshot = applyRoundCommand(current, event);
    await this.options.journal.saveSnapshotAndEnqueue(
      this.options.accountId,
      snapshot,
      event,
    );
    return snapshot;
  }

  private createEvent(
    roundId: string,
    baseVersion: number,
    type: RoundCommandType,
    payload: RoundCommandPayload,
    scorekeeperGeneration?: number,
  ): RoundCommandEvent {
    return {
      eventId: this.options.nextEventId(),
      roundId,
      deviceId: this.options.deviceId,
      deviceSequence: this.options.nextSequence(),
      baseVersion,
      type,
      occurredAt: this.now().toISOString(),
      payload,
      ...(scorekeeperGeneration === undefined ? {} : { scorekeeperGeneration }),
    };
  }

  private async requireSnapshot(roundId: string): Promise<RoundSnapshot> {
    const snapshot = await this.options.journal.getSnapshot(
      this.options.accountId,
      roundId,
    );
    if (!snapshot) {
      throw new Error("The local round does not exist.");
    }
    return snapshot;
  }
}
